import type { ReactElement, CSSProperties } from "react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AlertTriangle, Trash2, Loader2, type LucideIcon } from "lucide-react";

/**
 * Dialog variant options
 */
type DialogVariant = "danger" | "warning" | "default";

/**
 * ConfirmDialog component props
 */
interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  description?: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm?: () => void;
  loading?: boolean;
  variant?: DialogVariant;
}

/**
 * Variant style configuration
 */
interface VariantStyle {
  iconBg: string;
  iconColor: string;
  buttonBg: string;
  buttonHover: string;
}

const VARIANT_STYLES: Record<DialogVariant, VariantStyle> = {
  danger: {
    iconBg: "#FEE2E2",
    iconColor: "#EF4444",
    buttonBg: "linear-gradient(135deg, #EF4444 0%, #DC2626 100%)",
    buttonHover: "#DC2626",
  },
  warning: {
    iconBg: "#FEF3C7",
    iconColor: "#F59E0B",
    buttonBg: "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)",
    buttonHover: "#D97706",
  },
  default: {
    iconBg: "var(--st-border-light)",
    iconColor: "var(--st-primary)",
    buttonBg: "linear-gradient(135deg, var(--st-primary-hover) 0%, var(--st-primary) 100%)",
    buttonHover: "var(--st-primary)",
  },
};

/**
 * ConfirmDialog Component
 *
 * Reusable confirmation dialog component
 */
const ConfirmDialog = ({
  open,
  onOpenChange,
  title = "Are you sure?",
  description = "This action cannot be undone.",
  confirmText = "Delete",
  cancelText = "Cancel",
  onConfirm,
  loading = false,
  variant = "danger",
}: ConfirmDialogProps): ReactElement => {
  const styles = VARIANT_STYLES[variant] || VARIANT_STYLES.default;
  const Icon: LucideIcon = variant === "danger" ? Trash2 : AlertTriangle;

  const iconBgStyle: CSSProperties = { background: styles.iconBg };
  const iconStyle: CSSProperties = { color: styles.iconColor };
  const buttonStyle: CSSProperties = { background: styles.buttonBg };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="rounded-2xl border-0 p-0 overflow-hidden max-w-md">
        <div className="p-6">
          <AlertDialogHeader className="flex flex-col items-center text-center space-y-4">
            {/* Icon */}
            <div
              className="w-14 h-14 rounded-full flex items-center justify-center"
              style={iconBgStyle}
            >
              <Icon className="w-7 h-7" style={iconStyle} />
            </div>

            <AlertDialogTitle
              className="text-xl font-bold"
              style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-text-heading)" }}
            >
              {title}
            </AlertDialogTitle>

            <AlertDialogDescription
              className="text-sm leading-relaxed"
              style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-text-muted)" }}
            >
              {description}
            </AlertDialogDescription>
          </AlertDialogHeader>

          <AlertDialogFooter className="flex-row gap-3 mt-6 sm:space-x-0">
            <AlertDialogCancel
              className="flex-1 h-11 rounded-xl font-semibold border-0 dark:hover:bg-gray-600"
              style={{ background: "var(--st-bg-surface)", color: "var(--st-text-heading)" }}
              disabled={loading}
            >
              {cancelText}
            </AlertDialogCancel>

            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                onConfirm?.();
              }}
              className="flex-1 h-11 rounded-xl font-semibold text-white border-0 hover:opacity-90 transition-opacity"
              style={buttonStyle}
              disabled={loading}
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                confirmText
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </div>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default ConfirmDialog;
